import React, { useCallback, useImperativeHandle, useRef, useState } from 'react'
import BaseModal from '@/component/modal/BaseModal.tsx'
import Input from '@/component/input/Input.tsx'
import BorderButton from '@/component/button/BorderButton.tsx'
import SolidButton from '@/component/button/SolidButton.tsx'
import { apiChangePassword } from '@/api/auth/changePassword.ts'
import { toast } from 'react-toastify'
import { equalString, isNotEmpty } from '@/util/strings.ts'
import { useTranslation } from 'react-i18next'
import PasswordPolicyIndicator from '@/component/input/PasswordPolicyIndicator.tsx'
import { PASSWORD_MAX_LENGTH } from '@/util/passwordPolicy.ts'

export interface IProps {
    className?: string
}

const ChangePasswordModal = ({}: IProps, ref: any) => {
    const modalRef = useRef<any>()
    const { t } = useTranslation()

    const resetForm = () => {
        setPassword('')
        setNewPassword('')
        setNewPasswordRe('')
        setIsPolicyValid(false)
        setPolicyError('')
    }

    useImperativeHandle(ref, () => ({
        show: () => {
            resetForm()
            modalRef.current.show()
        },
        close: () => {
            modalRef.current.close()
        }
    }))

    const [password, setPassword] = useState('')
    const [newPassword, setNewPassword] = useState('')
    const [newPasswordRe, setNewPasswordRe] = useState('')
    const [isPolicyValid, setIsPolicyValid] = useState(false)
    const [policyError, setPolicyError] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const onPolicyValidChange = useCallback((valid: boolean) => setIsPolicyValid(valid), [])
    const passwordsMatch = isNotEmpty(newPasswordRe) && equalString(newPassword, newPasswordRe)
    const canSubmit = isNotEmpty(password)
        && isPolicyValid
        && passwordsMatch
        && !equalString(password, newPassword)
        && !isSubmitting

    const onClickSubmit = async () => {
        if (!canSubmit) {
            toast.error(t('changePasswordModal.submit.error.passwordNotSame'))
            return
        }

        setIsSubmitting(true)
        try {
            const { code, message } = await apiChangePassword({
                currentPassword: password,
                newPassword: newPassword
            })
            if (code == 200) {
                setPolicyError('')
                toast.success(t('changePasswordModal.submit.success'))
                modalRef.current?.close()
            } else {
                const reason = message || t('changePasswordModal.submit.error.failToChangePassword')
                setPolicyError(reason)
                toast.error(reason)
            }
        } catch (error) {
            const reason = error instanceof Error
                ? error.message
                : t('changePasswordModal.submit.error.failToChangePassword')
            setPolicyError(reason)
            toast.error(reason)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <BaseModal ref={modalRef} showCloseButton={false} width={'400'}>
            <div className={'flex items-center justify-center px-[30px] py-[40px]'}>
                <div className={'w-full grid gap-[10px] max-w-[263px] '}>
                    <p className={'text-center font-bold'}>{t('changePasswordModal.title')}</p>
                    <Input
                        className={'mt-[24px]'}
                        inputClassName={'w-full'}
                        value={password}
                        placeholder={t('changePasswordModal.current.placeholder')}
                        inputType={'password'}
                        maxLength={PASSWORD_MAX_LENGTH}
                        autoComplete={'current-password'}
                        onChanged={(text) => setPassword(text)}
                    />
                    <Input
                        inputClassName={'w-full'}
                        value={newPassword}
                        placeholder={t('changePasswordModal.new.placeholder')}
                        inputType={'password'}
                        maxLength={PASSWORD_MAX_LENGTH}
                        autoComplete={'new-password'}
                        error={policyError || undefined}
                        onChanged={(text) => {
                            setPolicyError('')
                            setNewPassword(text)
                        }}
                    />
                    <PasswordPolicyIndicator
                        password={newPassword}
                        onValidChange={onPolicyValidChange}
                    />
                    <Input
                        inputClassName={'w-full'}
                        value={newPasswordRe}
                        placeholder={t('changePasswordModal.newRe.placeholder')}
                        inputType={'password'}
                        maxLength={PASSWORD_MAX_LENGTH}
                        autoComplete={'new-password'}
                        onChanged={(text) => setNewPasswordRe(text)}
                    />
                    {isNotEmpty(newPasswordRe) && !passwordsMatch && (
                        <p className="text-xs text-red-500" role="alert">
                            {t('changePasswordModal.submit.error.passwordNotSame')}
                        </p>
                    )}
                    <div className={'flex gap-[10px] mt-[20px]'}>
                        <BorderButton
                            text={t('changePasswordModal.cancel')}
                            className={'flex-1'}
                            onClick={() => modalRef?.current?.close()}
                        />
                        <SolidButton
                            text={t('changePasswordModal.confirm')}
                            className={'flex-1'}
                            onClick={onClickSubmit}
                            disabled={!canSubmit}
                            isLoading={isSubmitting}
                        />
                    </div>
                </div>
            </div>
            <div></div>
        </BaseModal>
    )
}

export default React.forwardRef(ChangePasswordModal)
